/*
  De Tha Dol / A.Hebrides 002
  Shared behaviour for all dtd- sections. Every section's markup includes
  the same <script src> tag independently (sections are reorderable, so
  none can assume another one already loaded it) — the boot guard below
  is what keeps that from running this file once per section.
*/
(function () {
  "use strict";

  /* Every dtd- section includes this same <script src> tag independently
     (sections are reorderable/removable in the customiser, so none of them
     can assume another one will load it). Browsers execute a repeated
     <script src="same-url"> once per occurrence, not once total, so without
     this guard the whole file (and boot()) ran once per section on the
     page. That multiplied every IntersectionObserver, scroll listener and
     click handler, and occasionally raced the hero's mobile/video check
     into removing the video on a bad read. */
  if (window.__dtdCoreBooted) return;
  window.__dtdCoreBooted = true;

  function reducedMotionPreferred() {
    return (
      document.documentElement.classList.contains("dtd-force-reduced-motion") ||
      (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches)
    );
  }
  var reduceMotion = reducedMotionPreferred();

  /* ---------------------------------------------------------------------
     Smooth-scroll helper. Used by every CTA that jumps to a section, so
     Safari (patchy native CSS scroll-behavior support in places) gets a
     consistent JS-driven scroll instead of relying on the CSS alone.
     --------------------------------------------------------------------- */
  function scrollToTarget(id, openFormFirst) {
    var target = document.getElementById(id);
    if (!target) return;

    if (openFormFirst) {
      var details = target.matches("details") ? target : target.querySelector("details");
      if (details && !details.open) details.open = true;
    }

    // Opening the details above doesn't move target's own top offset (it
    // only grows content below it), so this can run synchronously rather
    // than waiting a frame for layout to settle.
    var top = target.getBoundingClientRect().top + window.pageYOffset;
    var offset = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
    window.scrollTo({
      top: top - offset,
      behavior: reducedMotionPreferred() ? "auto" : "smooth",
    });
  }

  function initScrollLinks(root) {
    var triggers = root.querySelectorAll("[data-dtd-scroll-to]");
    triggers.forEach(function (el) {
      el.addEventListener("click", function (e) {
        var id = el.getAttribute("data-dtd-scroll-to");
        if (!id) return;
        e.preventDefault();
        scrollToTarget(id, el.hasAttribute("data-dtd-open-form"));
      });
    });
  }

  /* ---------------------------------------------------------------------
     Reveal on scroll
     --------------------------------------------------------------------- */
  function initReveal(root) {
    var els = root.querySelectorAll("[data-dtd-reveal]");
    if (!els.length) return;

    if (reduceMotion || !("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("is-in"); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------------
     Scroll progress hairline
     --------------------------------------------------------------------- */
  function initProgress(root) {
    var bar = root.querySelector("[data-dtd-progress-bar]");
    if (!bar) return;
    var ticking = false;
    function update() {
      var h = document.documentElement;
      var scrolled = h.scrollTop || document.body.scrollTop;
      var height = (h.scrollHeight || document.body.scrollHeight) - h.clientHeight;
      var pct = height > 0 ? Math.min(scrolled / height, 1) : 0;
      bar.style.transform = "scaleX(" + pct + ")";
      ticking = false;
    }
    update();
    window.addEventListener(
      "scroll",
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    window.addEventListener("resize", update);
  }

  /* ---------------------------------------------------------------------
     Hero: line-by-line title reveal, deferred video, subtle parallax
     --------------------------------------------------------------------- */
  function initHero(hero) {
    // Stagger the title lines and footer without waiting on JS-heavy setup.
    requestAnimationFrame(function () {
      hero.classList.add("is-ready");
    });

    var lines = hero.querySelectorAll(".dtd-hero__line > span");
    lines.forEach(function (span, i) {
      span.style.setProperty("--dtd-delay", 0.12 * i + "s");
    });

    var mediaWrap = hero.querySelector("[data-dtd-hero-video]");
    var video = mediaWrap ? mediaWrap.querySelector("video") : null;
    var isMobile = window.matchMedia && window.matchMedia("(max-width: 47.99em)").matches;

    if (video) {
      if (reducedMotionPreferred() || isMobile) {
        // Reduced motion or mobile: never fetch or play video, poster only.
        video.remove();
      } else {
        var playAttempt = video.play();
        if (playAttempt && typeof playAttempt.catch === "function") {
          playAttempt.catch(function () {
            /* Autoplay was blocked; the poster frame remains a complete
               experience on its own, so no further action is needed. */
          });
        }
      }
    }

    if (!reduceMotion && mediaWrap) {
      var media = mediaWrap.querySelector("video, img");
      var ticking = false;
      function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          var rect = hero.getBoundingClientRect();
          var progress = Math.min(Math.max(-rect.top / (rect.height || 1), 0), 1);
          if (media) media.style.setProperty("--dtd-par", progress * 46 + "px");
          ticking = false;
        });
      }
      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();
    }
  }

  /* ---------------------------------------------------------------------
     Collapsible entry form (native <details>, forced open server-side
     when the form was just submitted, with or without errors)
     --------------------------------------------------------------------- */
  function initForm(details) {
    if (details.hasAttribute("data-dtd-force-open")) {
      window.requestAnimationFrame(function () {
        var top = details.getBoundingClientRect().top + window.pageYOffset - 24;
        window.scrollTo({ top: top, behavior: reducedMotionPreferred() ? "auto" : "smooth" });
      });
    }
  }

  /* ---------------------------------------------------------------------
     Preview toolbar. Only ever rendered by Liquid when request.design_mode
     is true, so this never runs on the live storefront.
     --------------------------------------------------------------------- */
  function initToolbar(toolbar) {
    var galleryBtn = toolbar.querySelector('[data-dtd-toolbar="gallery"]');
    var motionBtn = toolbar.querySelector('[data-dtd-toolbar="motion"]');
    var viewportBtns = toolbar.querySelectorAll("[data-dtd-toolbar-viewport]");
    var gallery = document.querySelector(".dtd-gallery");

    if (galleryBtn && gallery) {
      galleryBtn.addEventListener("click", function () {
        var showingMock = gallery.getAttribute("data-preview-state") === "populated";
        gallery.setAttribute("data-preview-state", showingMock ? "auto" : "populated");
        galleryBtn.setAttribute("aria-pressed", showingMock ? "false" : "true");
      });
    }

    if (motionBtn) {
      motionBtn.addEventListener("click", function () {
        var isForced = document.documentElement.classList.toggle("dtd-force-reduced-motion");
        motionBtn.setAttribute("aria-pressed", isForced ? "true" : "false");
      });
    }

    viewportBtns.forEach(function (btn) {
      btn.addEventListener("click", function () {
        var size = btn.getAttribute("data-dtd-toolbar-viewport");
        document.body.classList.remove("dtd-preview-tablet", "dtd-preview-mobile");
        document.body.classList.add("dtd-preview-frame");
        viewportBtns.forEach(function (b) { b.setAttribute("aria-pressed", "false"); });
        if (size === "tablet") document.body.classList.add("dtd-preview-tablet");
        if (size === "mobile") document.body.classList.add("dtd-preview-mobile");
        btn.setAttribute("aria-pressed", "true");
      });
    });
  }

  /* ---------------------------------------------------------------------
     Gallery carousel. Prev/next buttons scroll whichever track (real or
     the design_mode-only mock) is currently visible.
     --------------------------------------------------------------------- */
  function initGalleryCarousel(section) {
    var prevBtn = section.querySelector("[data-dtd-gallery-prev]");
    var nextBtn = section.querySelector("[data-dtd-gallery-next]");
    if (!prevBtn || !nextBtn) return;

    function visibleTrack() {
      var tracks = section.querySelectorAll("[data-dtd-gallery-track]");
      for (var i = 0; i < tracks.length; i++) {
        if (getComputedStyle(tracks[i]).display !== "none") return tracks[i];
      }
      return null;
    }

    function updateButtons() {
      var track = visibleTrack();
      if (!track) {
        prevBtn.disabled = true;
        nextBtn.disabled = true;
        return;
      }
      var max = track.scrollWidth - track.clientWidth;
      prevBtn.disabled = track.scrollLeft <= 4;
      nextBtn.disabled = max <= 4 || track.scrollLeft >= max - 4;
    }

    function scrollBy(direction) {
      var track = visibleTrack();
      if (!track) return;
      var card = track.querySelector(".dtd-clip");
      var step = card ? card.getBoundingClientRect().width + 20 : track.clientWidth * 0.8;
      track.scrollBy({
        left: step * direction,
        behavior: reducedMotionPreferred() ? "auto" : "smooth",
      });
    }

    prevBtn.addEventListener("click", function () { scrollBy(-1); });
    nextBtn.addEventListener("click", function () { scrollBy(1); });

    section.querySelectorAll("[data-dtd-gallery-track]").forEach(function (track) {
      track.addEventListener("scroll", updateButtons, { passive: true });
    });
    window.addEventListener("resize", updateButtons);

    var galleryToggle = document.querySelector('[data-dtd-toolbar="gallery"]');
    if (galleryToggle) galleryToggle.addEventListener("click", function () {
      setTimeout(updateButtons, 20);
    });

    updateButtons();
  }

  /* ---------------------------------------------------------------------
     Boot
     --------------------------------------------------------------------- */
  function boot() {
    var toolbar = document.querySelector("[data-dtd-toolbar]");
    if (toolbar) initToolbar(toolbar);

    document.querySelectorAll(".dtd").forEach(function (root) {
      initReveal(root);
      initProgress(root);
      initScrollLinks(root);

      var hero = root.querySelector(".dtd-hero");
      if (hero) initHero(hero);

      var form = root.querySelector(".dtd-form");
      if (form) initForm(form);

      var gallery = root.querySelector(".dtd-gallery");
      if (gallery) initGalleryCarousel(gallery);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
